import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { FilterType } from '~/components/shared'
import { HealthProcedureVariantProps } from '~/constants'
import {
  createDateFilterTagWith,
  createSearchTermTagWith,
  createSelectedPeriodFromPrevious,
  createTypeFiltersTagWith,
  EHealthProceduresTypes,
  IFetchHealthProcedures,
  IFetchHealthProceduresParams,
  IFilterTag,
  IGetHealthProcedures,
  IHealthProcedures,
  ISelectedPeriod,
  MedicalViewsType,
  yearsPeriod
} from '~/interfaces'
import { fetchPatientHealthProcedures, removeUnsupportedEvents, message } from '~/services'
import { identifyInProgressEvents, transformDate } from '~/utils'

interface RadiologyHealthProceduresProviderProps {
  children: React.ReactNode
}

export interface IRadiologyHealthProceduresContextHelpers {
  isLoadingTimeline: boolean
  errorTimeline: string
}

const defaultValue = {
  healthProcedures: [] as IHealthProcedures[],
  filterTags: [] as IFilterTag[],
  selectedPeriod: yearsPeriod,

  // helpers
  isLoadingTimeline: false,
  errorTimeline: 'idle'
}

export interface IRadiologyHealthProceduresContextProps extends IRadiologyHealthProceduresContextHelpers {
  displayedHealthProcedures: IHealthProcedures[]
  getHealthProcedures: IGetHealthProcedures

  selectedPeriod: ISelectedPeriod
  updateSelectedPeriod: (newSelectedPeriod: ISelectedPeriod) => void

  modalHealthProcedure: IHealthProcedures | null
  selectMedicalEventForDetailing: (healthProcedures: IHealthProcedures | null) => void
  handleFilterHealthProceduresPerType: (types: FilterType[]) => void
  handleFilterHealthProceduresPerDate: (rangeDates: Date[]) => void
  initializeSidePanel: () => void
  dateFiltersActive: Date[]
  typeFiltersActive: string[]

  openConductCardList: boolean
  setOpenConductCardList: Dispatch<SetStateAction<boolean>>
  openOmicsCardList: boolean
  setOpenOmicsCardList: Dispatch<SetStateAction<boolean>>

  showHealthProcedureDetails: () => boolean
  closeHealthProcedureDetails: () => void

  hasDateFiltersActive: boolean
  hasTypeFiltersActive: boolean

  filterTags: IFilterTag[]
  setFilterTagToAdmissionEvent: (newTag: IFilterTag) => void
  removeFilterTag: (tag: IFilterTag) => void
  resetFilterTags: () => void

  filterHealthProceduresByTerm: (term: IFilterTag) => void

  sidePanelEvents: IHealthProcedures[]
  updateSidePanelEvents: (events: IHealthProcedures[]) => void
  resetSidePanelEvents: () => void
}

const RadiologyHealthProceduresContext = React.createContext<IRadiologyHealthProceduresContextProps>(
  {} as IRadiologyHealthProceduresContextProps
)

function RadiologyHealthProceduresProvider({ children }: RadiologyHealthProceduresProviderProps): JSX.Element {
  const [isLoadingTimeline, setIsLoadingTimeline] = useState<boolean>(defaultValue.isLoadingTimeline)
  const [errorTimeline, setErrorTimeline] = useState<string>(defaultValue.errorTimeline)

  const [healthProcedures, setHealthProcedures] = useState<IHealthProcedures[]>(defaultValue.healthProcedures)
  const [displayedHealthProcedures, setDisplayedHealthProcedures] = useState<IHealthProcedures[]>(
    defaultValue.healthProcedures
  )
  const [selectedPeriod, setSelectedPeriod] = useState<ISelectedPeriod>(defaultValue.selectedPeriod)
  const [modalHealthProcedure, setModalHealthProcedure] = useState<IHealthProcedures | null>(null)
  const [dateFiltersActive, setDateFiltersActive] = useState<Date[]>([])
  const [typeFiltersActive, setTypeFiltersActive] = useState<string[]>([])
  const [filterTags, setFilterTags] = useState<IFilterTag[]>(defaultValue.filterTags)

  const [openConductCardList, setOpenConductCardList] = useState<boolean>(false)
  const [openOmicsCardList, setOpenOmicsCardList] = useState<boolean>(false)

  const [sidePanelEvents, setSidePanelEvents] = useState<IHealthProcedures[]>([])
  const updateSidePanelEvents = useCallback((events: IHealthProcedures[]) => setSidePanelEvents(events), [])
  const resetSidePanelEvents = useCallback(() => {
    updateSidePanelEvents(healthProcedures)
  }, [updateSidePanelEvents, healthProcedures])

  const getHealthProcedures: IGetHealthProcedures = async (
    params: IFetchHealthProceduresParams,
    selectedMedicalView: MedicalViewsType
  ) => {
    try {
      setIsLoadingTimeline(true)
      let response = await fetchPatientHealthProcedures<IFetchHealthProcedures>(selectedMedicalView)(params)
      response = removeUnsupportedEvents(response)
      response = identifyInProgressEvents(response)
      if (response.length <= 0) {
        setHealthProcedures([])
        setErrorTimeline(message('timeline.not_found'))
        return
      }
      setHealthProcedures(response)
      setDisplayedHealthProcedures(response)
      setErrorTimeline('idle')
    } catch (error) {
      setHealthProcedures([])
      setErrorTimeline(message('historic.historicEncounterError'))
    } finally {
      setIsLoadingTimeline(false)
    }
  }

  const initializeSidePanel = useCallback(() => {
    resetSidePanelEvents()
  }, [resetSidePanelEvents])

  useEffect(() => {
    if (displayedHealthProcedures.length > 0) {
      initializeSidePanel()
    }
  }, [displayedHealthProcedures, initializeSidePanel])

  const updateSelectedPeriod = (newSelectedPeriod: ISelectedPeriod): void => {
    setSelectedPeriod({ ...newSelectedPeriod })
  }

  const selectMedicalEventForDetailing = (healthProcedures: IHealthProcedures | null) => {
    setModalHealthProcedure(healthProcedures)
  }

  const updateTypeFilterTags = useCallback((filters: FilterType[]): void => {
    const tagsOfTypeFilters = filters.map(filter => {
      const filterColor = HealthProcedureVariantProps[filter.type[0]]?.color
      return createTypeFiltersTagWith(filter.text, filterColor, filter.type)
    })

    setFilterTags(existingTags => {
      const existentTags = existingTags.filter(tag => tag.type !== 'typeFilter')
      return [...existentTags, ...tagsOfTypeFilters]
    })
  }, [])

  const handleFilterHealthProceduresPerType = useCallback(
    (typeFilters: FilterType[]) => {
      const activeTypes = typeFilters.flatMap(filter => filter.type)
      setTypeFiltersActive(activeTypes)
      updateTypeFilterTags(typeFilters)
      initializeSidePanel()
      setOpenOmicsCardList(true)
      setOpenConductCardList(true)
    },
    [initializeSidePanel, updateTypeFilterTags]
  )

  const removeTypeFilterTag = useCallback(
    (tag: IFilterTag): void => {
      const newTypeFiltersActive = typeFiltersActive.filter(type => {
        const tagTypes = tag.id.split('-')
        return !tagTypes.includes(type)
      })
      setTypeFiltersActive(newTypeFiltersActive)
    },
    [typeFiltersActive]
  )

  const hasTypeFiltersActive: boolean = typeFiltersActive.length > 0

  const updateDateFilterTags = useCallback(
    (rangeDates: Date[]): void => {
      const tagsWithoutDateFilter = filterTags.filter(tag => tag.type !== 'dateFilter')
      const isNotAValidRangeOfDates = rangeDates.length <= 1
      if (isNotAValidRangeOfDates) {
        setFilterTags(tagsWithoutDateFilter)
        return
      }

      const formattedDates = rangeDates.map(date => transformDate({ date }))
      const tagDescription = formattedDates.join(' - ')
      setFilterTags([...tagsWithoutDateFilter, createDateFilterTagWith(tagDescription)])
    },
    [filterTags]
  )

  const handleFilterHealthProceduresPerDate = useCallback(
    (rangeDates: Date[]) => {
      const isAValidRangeOfDates = rangeDates.length !== 1

      if (isAValidRangeOfDates) {
        setDateFiltersActive(rangeDates)
        updateDateFilterTags(rangeDates)
        initializeSidePanel()
        setOpenOmicsCardList(true)
        setOpenConductCardList(true)
      }
    },
    [initializeSidePanel, updateDateFilterTags]
  )

  const removeDateFilterTag = (): void => {
    setDateFiltersActive([])
  }

  const hasDateFiltersActive: boolean = dateFiltersActive.length > 0

  const showHealthProcedureDetails = (): boolean => {
    return !!modalHealthProcedure
  }

  const closeHealthProcedureDetails = (): void => {
    setModalHealthProcedure(null)
  }

  const setFilterTagToAdmissionEvent = (newTag: IFilterTag): void => {
    const tagsWithoutAdmissionFilterTag = filterTags.filter(tag => tag.id !== EHealthProceduresTypes.ADMISSION)
    const admissionFilterTag = filterTags.filter(tag => tag.id === EHealthProceduresTypes.ADMISSION)
    setFilterTags([...tagsWithoutAdmissionFilterTag, newTag])
    if (admissionFilterTag.length) {
      removeTypeFilterTag(admissionFilterTag[0])
    }
  }

  const removeAdmissionEventTag = useCallback(() => {
    setSelectedPeriod(selectedPeriod => ({
      ...createSelectedPeriodFromPrevious(selectedPeriod.previousPeriod)
    }))
  }, [])

  const removeFilterTag = useCallback(
    (tag: IFilterTag): void => {
      setFilterTags(existingTags => existingTags.filter(ft => ft.id !== tag.id))

      switch (tag.type) {
        case 'admissionEvent':
          return removeAdmissionEventTag()
        case 'dateFilter':
          return removeDateFilterTag()
        case 'typeFilter':
          return removeTypeFilterTag(tag)
      }
    },
    [removeAdmissionEventTag, removeTypeFilterTag]
  )

  const resetFilterTags = useCallback((): void => {
    setFilterTags(defaultValue.filterTags)

    setTypeFiltersActive([])
    removeDateFilterTag()
  }, [])

  const filterHealthProceduresByTerm = useCallback(
    (term: IFilterTag) => {
      const tagByTerm = createSearchTermTagWith(term.text)
      setFilterTags(existingTags => [...existingTags, tagByTerm])
      initializeSidePanel()
      setOpenOmicsCardList(true)
      setOpenConductCardList(true)
    },
    [initializeSidePanel]
  )

  return (
    <RadiologyHealthProceduresContext.Provider
      value={{
        displayedHealthProcedures,
        getHealthProcedures,

        selectedPeriod,
        updateSelectedPeriod,

        modalHealthProcedure,
        selectMedicalEventForDetailing,
        handleFilterHealthProceduresPerDate,
        handleFilterHealthProceduresPerType,
        initializeSidePanel,
        dateFiltersActive,
        typeFiltersActive,

        errorTimeline,
        isLoadingTimeline,
        hasDateFiltersActive,
        hasTypeFiltersActive,

        openConductCardList,
        setOpenConductCardList,
        openOmicsCardList,
        setOpenOmicsCardList,

        showHealthProcedureDetails,
        closeHealthProcedureDetails,

        filterTags,
        setFilterTagToAdmissionEvent,
        removeFilterTag,
        resetFilterTags,

        filterHealthProceduresByTerm,

        sidePanelEvents,
        updateSidePanelEvents,
        resetSidePanelEvents
      }}
    >
      {children}
    </RadiologyHealthProceduresContext.Provider>
  )
}

const useRadiologyHealthProcedures = (): IRadiologyHealthProceduresContextProps => {
  return React.useContext(RadiologyHealthProceduresContext)
}

export { RadiologyHealthProceduresContext, RadiologyHealthProceduresProvider, useRadiologyHealthProcedures }
